import type {StoreId} from "../../../stem-core/state/State";
import {BaseEnum, makeEnum} from "../../../stem-core/state/BaseEnum";
import {field} from "../../../stem-core/state/StoreField";
import {type CurrencyLike, Money} from "../../../stem-core/localization/Money";
import {setObjectPrototype, unwrapArray} from "../../../stem-core/base/Utils";
import {Currency} from "../../../stem-core/localization/CurrencyStore";
import {UserAddress} from "../UserAddressStore";

function addressPartMatches(entry: string, filter?: string | string[]): boolean {
    if (filter == null) {
        return true;
    }
    if (Array.isArray(filter)) {
        for (const value of filter) {
            if (String(entry).toLowerCase() === String(value).toLowerCase()) { // String or numbers are the same
                return true;
            }
        }
        return false;
    }

    return String(entry).toLowerCase() === String(filter).toLowerCase();
}

@makeEnum
export class ShippingPriceType extends BaseEnum {
    static SINGLE_COUNTRY;
    static MULTI_COUNTRY;
    static WORLDWIDE;
}


// TODO @branch where to move this?
// Mode can be single country, multi country, worldwide ("*")
// Worldwide has countryCode = "*", matches anything
// Multicountry matches an explicit list of countries (and just that)
// Single country can have further filters (region, postalCode).
// TODO @Mihai support EU as country code
// The JSON the server stores per entry (api/payments/models/base.py). loadArray turns these into
// ShippingPriceEntry instances, supplying the currency from whatever owns them. Note the server is
// narrower than the class below: region is a plain string there, postalCode always a list
export interface RawShippingPriceEntry {
    amount: number;
    countryCode: string | string[];
    region?: string;
    postalCode?: string[];
    // Not sent by the server: loadArray stamps it on from whatever owns the entries
    currencyId?: StoreId;
}

export class ShippingPriceEntry {
    @field(Currency) currency: Currency; // From the subscription plan
    amount: number = 0;
    countryCode: string | string[] = "US"; // Array or String
    region?: string | string[]; // Optional String
    postalCode?: string | string[]; // Optional String or Array

    getType(): ShippingPriceType {
        if (this.isWorldwide()) {
            return ShippingPriceType.WORLDWIDE;
        }
        if (this.isMultiCountry()) {
            return ShippingPriceType.MULTI_COUNTRY;
        }
        return ShippingPriceType.SINGLE_COUNTRY;
    }

    isWorldwide(): boolean {
        return this.countryCode === "*";
    }

    isMultiCountry(): boolean {
        return Array.isArray(this.countryCode);
    }

    isSingleCountry(): boolean {
        return !this.isWorldwide() && !this.isMultiCountry();
    }

    matchesAddress(address: UserAddress): boolean {
        if (this.isWorldwide()) {
            return true;
        }

        if (!addressPartMatches(address.countryCode, this.countryCode)) {
            return false;
        }

        if (this.isSingleCountry()) {
            if (!addressPartMatches(address.state, this.region) || !addressPartMatches(address.postalCode, this.postalCode)) {
                return false;
            }
        }

        return true;
    }

    getPrice(defaultCurrency?: CurrencyLike): Money {
        const currency = this.currency || defaultCurrency;
        return new Money(this.amount, currency);
    }

    formatLocation(): string {
        if (this.isWorldwide()) {
            return "Worldwide";
        }
        // TODO @branch need longer country names
        if (this.isMultiCountry()) {
            // TODO @branch truncate long lists (and X more countries).
            return (this.countryCode as string[]).join(", ");
        }
        const regionFormat = this.region && "Region " + (Array.isArray(this.region) ? this.region.join(", ") : this.region);
        const postalCodeFormat = this.postalCode && "Post code " + (Array.isArray(this.postalCode) ? this.postalCode.join(", "): this.postalCode);
        return unwrapArray([this.countryCode, regionFormat, postalCodeFormat]).join("; ");
    }

    // TODO @Mihai @branch make this iterable
    // entries stays open: the editor hands back instances it already loaded, so this takes both shapes
    static loadArray(entries: any[], currencyId?: StoreId): ShippingPriceEntry[] {
        if (!Array.isArray(entries)) {
            return [];
        }
        for (let entry of entries) {
            // TODO: @branch kinda voodoo
            setObjectPrototype(entry, this);
            if (currencyId && !entry.currencyId) {
                entry.currencyId = currencyId;
            }
        }
        return entries;
    }

    static clone(entry: any, inplace: boolean = false): ShippingPriceEntry {
        const obj = {
            ...entry,
        };
        // TODO @branch support inplace
        return setObjectPrototype(obj, this);
    }
}

// TODO @branch implement as method of shipping prices field
export function CalculateShippingPrice(address: UserAddress, shippingPrices: ShippingPriceEntry[]): Money {
    for (const shippingPrice of shippingPrices) {
        if (shippingPrice.matchesAddress(address)) {
            return shippingPrice.getPrice();
        }
    }
    return new Money(0, shippingPrices[0]?.currency || 1);
}
